import { AlertTriangle, Shield, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

// Dismissible fraud warning banner for top of pages
export function FraudBanner({ variant = 'default' }) {
  const [dismissed, setDismissed] = useState(false);
  
  if (dismissed) return null;

  const variants = {
    default: {
      bg: 'bg-orange-500/10 border-orange-500/30',
      icon: 'text-orange-500',
      text: 'text-orange-200'
    },
    danger: {
      bg: 'bg-red-500/10 border-red-500/30',
      icon: 'text-red-500',
      text: 'text-red-200'
    },
    info: {
      bg: 'bg-blue-500/10 border-blue-500/30',
      icon: 'text-blue-400',
      text: 'text-blue-200'
    }
  };

  const style = variants[variant];

  return (
    <div className={`${style.bg} border rounded-lg p-3 mb-4`} data-testid="fraud-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 ${style.icon} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <p className={`text-sm ${style.text}`}>
            <strong>Stay Safe:</strong> Never send money outside the platform. Use our secure escrow payment for all transactions.{' '}
            <Link to="/help#safety" className="underline hover:no-underline">Learn more about staying safe</Link>
          </p>
        </div>
        <button 
          onClick={() => setDismissed(true)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Non-dismissible warning for critical areas (checkout, payment)
export function FraudWarning({ type = 'payment' }) {
  const warnings = {
    payment: {
      title: 'Secure Payment Reminder',
      message: 'Your payment is protected by escrow. Funds are only released to the seller after you confirm vehicle receipt. Never pay sellers directly outside this platform.',
      icon: Shield,
      bg: 'bg-trust-green/10 border-trust-green/30',
      iconColor: 'text-trust-green'
    },
    listing: {
      title: 'Listing Verification',
      message: 'This listing has been verified by our AI system. However, always inspect the vehicle in person before confirming any escrow payment. Check the VIN matches the documents.',
      icon: Shield,
      bg: 'bg-blue-500/10 border-blue-500/30',
      iconColor: 'text-blue-400'
    },
    chat: {
      title: 'Communication Safety',
      message: 'Keep all communication within the app. Be cautious of requests to continue conversations on WhatsApp or other platforms. Report suspicious behavior immediately.',
      icon: AlertTriangle,
      bg: 'bg-orange-500/10 border-orange-500/30',
      iconColor: 'text-orange-500'
    },
    create: {
      title: 'Honest Listings Policy',
      message: 'All listings are verified by AI. False information, hidden defects, or manipulated photos will result in listing removal and potential account suspension.',
      icon: AlertTriangle,
      bg: 'bg-orange-500/10 border-orange-500/30',
      iconColor: 'text-orange-500'
    }
  };

  const warning = warnings[type];
  const IconComponent = warning.icon;

  return (
    <div className={`${warning.bg} border rounded-lg p-4`} data-testid={`fraud-warning-${type}`}>
      <div className="flex items-start gap-3">
        <IconComponent className={`w-5 h-5 ${warning.iconColor} flex-shrink-0 mt-0.5`} />
        <div>
          <h4 className="font-semibold text-sm mb-1">{warning.title}</h4>
          <p className="text-sm text-muted-foreground">{warning.message}</p>
        </div>
      </div>
    </div>
  );
}

// Inline warning text for forms
export function FraudDisclaimer() {
  return (
    <div className="text-xs text-muted-foreground bg-card/50 border border-border/50 rounded-lg p-3 mt-4">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="mb-2">
            <strong>Fraud Prevention Notice:</strong> AutoTrust Ghana takes fraud seriously.
          </p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground/80">
            <li>All transactions should use our escrow system</li>
            <li>Never share bank details or send money directly</li>
            <li>Verify vehicle documents before confirming delivery</li>
            <li>Report suspicious listings or users immediately</li>
          </ul>
          <p className="mt-2">
            <Link to="/legal" className="text-gold hover:underline">Terms of Service</Link> • <Link to="/help#safety" className="text-gold hover:underline">Safety Guide</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default FraudBanner;
